/**
 * A short set of mutually exclusive choices, and the table-or-cards toggle.
 *
 * Two treatments of the same idea existed: the matrix filter as three loose
 * buttons, and a `join` for view mode. Neither reported which one was chosen to
 * anything but the eye.
 *
 * @module components/ui/SegmentedControl
 */

import React, { useCallback, useState } from "react";

import { Icon } from "./Icon";
import { useMediaQuery } from "./useMediaQuery";

export interface SegmentedOption<Value extends string> {
  value: Value;
  label: string;
  icon?: string;
  "data-testid"?: string;
}

export interface SegmentedControlProps<Value extends string> {
  options: readonly SegmentedOption<Value>[];
  value: Value;
  onChange: (value: Value) => void;
  /** Names the set, since the options alone do not say what they filter. */
  "aria-label": string;
  size?: "xs" | "sm" | "md";
  className?: string;
  "data-testid"?: string;
}

/**
 * @example
 * ```tsx
 * <SegmentedControl aria-label="Show" options={scopes} value={scope} onChange={setScope} />
 * ```
 */
export function SegmentedControl<Value extends string>({
  options,
  value,
  onChange,
  "aria-label": label,
  size = "sm",
  className = "",
  "data-testid": testId,
}: SegmentedControlProps<Value>): React.ReactElement {
  return (
    <div
      role="group"
      aria-label={label}
      className={`join rounded-field border border-base-300 ${className}`.trim()}
      data-testid={testId}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            // aria-pressed rather than a class alone: the selection has to
            // reach a screen reader, not only the eye.
            aria-pressed={selected}
            onClick={() => {
              onChange(option.value);
            }}
            className={[
              "join-item btn",
              { xs: "btn-xs", sm: "btn-sm", md: "" }[size],
              selected ? "btn-active" : "btn-ghost",
            ]
              .filter(Boolean)
              .join(" ")}
            data-testid={option["data-testid"]}
          >
            {option.icon && <Icon name={option.icon} aria-hidden />}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export type ListMode = "table" | "cards";

/**
 * Below `md` a table cannot fit: nine columns become two, and what is left is
 * a row of truncated cells with the page scrolling sideways under them. Cards
 * are the only shape that works there, so the mode is forced rather than
 * offered — `ListToggle` hides itself over the same query.
 */
const CARDS_ONLY = "(max-width: 47.999rem)";

/**
 * Whether the viewport is too narrow for a table.
 *
 * Exported so a list that offers no choice can follow the same breakpoint the
 * toggle hides at, rather than picking its own.
 */
export function useCardsOnlyViewport(): boolean {
  return useMediaQuery(CARDS_ONLY);
}

/**
 * Remember how someone chose to look at one particular list.
 *
 * Per list rather than globally: a schedule reads better as a table and a
 * module picker as cards, and someone who says so once should not have to
 * repeat it on every visit.
 *
 * @param storageKey - Identifies the list, e.g. `programme-schedule`.
 * @param initial - The mode to use when nothing is stored.
 * @returns The current mode and a setter that persists it.
 */
export function useListMode(
  storageKey: string,
  initial: ListMode = "table",
): [ListMode, (mode: ListMode) => void] {
  // Read once, as the initial state, rather than in an effect: an effect would
  // render the default first and then correct it, which flashes the wrong view.
  // The key is expected to be constant for the life of the list.
  const [mode, setMode] = useState<ListMode>(() => {
    try {
      const stored = window.localStorage.getItem(`sqViewMode_${storageKey}`);
      return stored === "table" || stored === "cards" ? stored : initial;
    } catch {
      // A blocked or unavailable store is not a reason to fail the list; the
      // preference simply does not persist.
      return initial;
    }
  });

  const choose = useCallback(
    (next: ListMode) => {
      setMode(next);
      try {
        window.localStorage.setItem(`sqViewMode_${storageKey}`, next);
      } catch {
        // As above.
      }
    },
    [storageKey],
  );

  // The stored preference is forced, not overwritten, so it returns when the
  // viewport does.
  const narrow = useCardsOnlyViewport();

  return [narrow ? "cards" : mode, choose];
}

export interface ListToggleProps {
  value: ListMode;
  onChange: (mode: ListMode) => void;
  className?: string;
  "data-testid"?: string;
}

/**
 * The table-or-cards switch.
 *
 * Hidden below `md`, where `useListMode` forces cards, so the control never
 * offers a choice that cannot be honoured.
 */
export function ListToggle({
  value,
  onChange,
  className = "",
  "data-testid": testId,
}: ListToggleProps): React.ReactElement {
  return (
    <SegmentedControl<ListMode>
      aria-label="View mode"
      options={[
        { value: "table", label: "Table", icon: "table", "data-testid": "list-toggle-table" },
        { value: "cards", label: "Cards", icon: "cards-three", "data-testid": "list-toggle-cards" },
      ]}
      value={value}
      onChange={onChange}
      className={`max-md:hidden ${className}`.trim()}
      data-testid={testId}
    />
  );
}
